<div class="theme-settings-card">
  <div class="theme-header">
    <div>
      <h2>{{ 'SHOP_COMPANY_THEME' | translate }}</h2>
      <p>{{ 'SHOP_COMPANY_THEME_INHERIT_HELP' | translate }}</p>
    </div>
    <button
      class="primary-btn"
      type="button"
      [disabled]="saving || parentSaving || loading"
      (click)="saveTheme()"
    >
      <span
        class="spinner-border spinner-border-sm"
        role="status"
        *ngIf="saving || parentSaving"
      ></span>
      {{ (saving || parentSaving ? 'SAVING' : 'SAVE') | translate }}
    </button>
  </div>

  <div class="alert alert-danger" *ngIf="errorMessage">
    {{ errorMessage | translate }}
  </div>
  <div class="alert alert-success" *ngIf="successMessage">
    {{ successMessage | translate }}
  </div>
  <div class="loading" *ngIf="loading">
    <div class="spinner-border" role="status"></div>
  </div>

  <form [formGroup]="themeForm" *ngIf="!loading">
    <section class="inheritance-section">
      <label class="toggle-row">
        <input
          type="checkbox"
          [checked]="useParentTheme"
          (change)="toggleParentTheme()"
        />
        <span>{{ 'USE_PARENT_STORE_THEME' | translate }}</span>
      </label>
      <p class="inheritance-help" *ngIf="useParentTheme">
        {{ 'SHOP_COMPANY_USES_PARENT_THEME_HELP' | translate }}
      </p>
    </section>

    <section>
      <h3>{{ 'LOGOS_SECTION_TITLE' | translate }}</h3>
      <div class="asset-grid">
        <ng-container *ngFor="let asset of assetFields">
          <div class="asset-card" *ngIf="asset.display === 'logo'">
            <label>{{ asset.label | translate }}</label>
            <div class="asset-preview">
              <img
                *ngIf="assetPreviews[asset.key]"
                [src]="assetPreviews[asset.key]"
                [alt]="asset.label | translate"
              />
              <span *ngIf="!assetPreviews[asset.key]">
                {{ 'NO_IMAGE_SELECTED' | translate }}
              </span>
            </div>
            <input
              class="form-control"
              type="file"
              accept="image/*"
              (change)="selectAsset(asset.key, $event)"
              [disabled]="useParentTheme"
            />
          </div>
        </ng-container>
      </div>
    </section>

    <section class="brand-row-section">
      <div class="brand-row">
        <div class="theme-field font-card">
          <label for="shopFontFamily">{{ 'FONTS' | translate }}</label>
          <select
            id="shopFontFamily"
            class="form-select"
            [ngModel]="selectedFontFamily"
            [ngModelOptions]="{ standalone: true }"
            [disabled]="useParentTheme"
            (ngModelChange)="onFontChange($event)"
          >
            <option [ngValue]="null">{{ 'SELECT_FONT' | translate }}</option>
            <option *ngFor="let font of fonts" [value]="font.fontFamily">
              {{ font.fontName }}
            </option>
          </select>
          <div
            class="font-preview"
            *ngIf="selectedFontFamily"
            [ngStyle]="{ 'font-family': selectedFontFamily }"
          >
            <strong>{{ getSelectedFontName() }}</strong>
            <span>{{ 'PREVIEW' | translate }}: The quick brown fox jumps over the lazy dog</span>
          </div>
        </div>

        <div class="asset-card banner-card">
          <label>{{ bannerAssetField.label | translate }}</label>
          <div class="asset-preview banner-preview">
            <img
              *ngIf="assetPreviews[bannerAssetField.key]"
              [src]="assetPreviews[bannerAssetField.key]"
              [alt]="bannerAssetField.label | translate"
            />
            <span *ngIf="!assetPreviews[bannerAssetField.key]">
              {{ 'NO_IMAGE_SELECTED' | translate }}
            </span>
          </div>
          <input
            class="form-control"
            type="file"
            accept="image/*"
            (change)="selectAsset(bannerAssetField.key, $event)"
            [disabled]="useParentTheme"
          />
        </div>
      </div>
    </section>

    <section>
      <h3>{{ 'COLORS' | translate }}</h3>
      <div class="theme-visual-grid">
        <div class="theme-field" *ngFor="let field of colorFields">
          <label [for]="field.key">{{ field.label | translate }}</label>
          <div class="color-picker">
            <div class="container-image">
              <div
                class="colorful-svg"
                [style.--svg-color]="themeForm.get(field.key)?.value || '#454545'"
              ></div>
              <div class="info-image">
                <img
                  [src]="field.referenceImage"
                  [alt]="field.label | translate"
                />
              </div>
            </div>
            <output class="data-output">
              {{ themeForm.get(field.key)?.value || 'NO_VALUE' | translate }}
            </output>
          </div>
          <div class="color-row">
            <input
              type="color"
              [id]="field.key"
              [formControlName]="field.key"
              [value]="themeForm.get(field.key)?.value || '#EA803F'"
              [disabled]="useParentTheme"
            />
            <input
              class="form-control"
              type="text"
              [formControlName]="field.key"
              placeholder="#EA803F"
            />
          </div>
        </div>
        <ng-container *ngFor="let field of backgroundFields">
          <div class="theme-field background-card">
            <label>{{ field.label | translate }}</label>
            <div
              class="background-preview"
              [style.background]="buildBackgroundStyle(getBackground(field.key))"
            ></div>
            <div class="mode-buttons">
              <button
                type="button"
                [disabled]="useParentTheme"
                (click)="setBackgroundMode(field.key, 'solid')"
              >
                Solid
              </button>
              <button
                type="button"
                [disabled]="useParentTheme"
                (click)="setBackgroundMode(field.key, 'gradient')"
              >
                Gradient
              </button>
            </div>

            <div *ngIf="getBackground(field.key)?.mode === 'solid'">
              <input
                type="color"
                [value]="getBackground(field.key)?.solidColor || '#EA803F'"
                [disabled]="useParentTheme"
                (input)="
                  updateBackgroundColor(
                    field.key,
                    $any($event.target).value
                  )
                "
              />
            </div>

            <div
              class="gradient-controls"
              *ngIf="
                getBackground(field.key)?.mode === 'gradient' &&
                getBackground(field.key)?.gradient
              "
            >
              <label>{{ 'ANGLE' | translate }}</label>
              <input
                class="form-control"
                type="number"
                min="0"
                max="360"
                [value]="getBackground(field.key)?.gradient?.angle"
                [disabled]="useParentTheme"
                (input)="
                  updateGradientAngle(
                    field.key,
                    $any($event.target).value
                  )
                "
              />
              <div
                class="gradient-stop"
                *ngFor="
                  let stop of getBackground(field.key)?.gradient?.stops;
                  let index = index
                "
              >
                <input
                  type="color"
                  [value]="stop.color"
                  [disabled]="useParentTheme"
                  (input)="
                    updateBackgroundColor(
                      field.key,
                      $any($event.target).value,
                      index
                    )
                  "
                />
                <input
                  class="form-control"
                  type="number"
                  min="0"
                  max="100"
                  [value]="stop.position"
                  [disabled]="useParentTheme"
                  (input)="
                    updateGradientPosition(
                      field.key,
                      index,
                      $any($event.target).value
                    )
                  "
                />
                <span>%</span>
                <button
                  type="button"
                  class="stop-btn"
                  [disabled]="
                    useParentTheme ||
                    (getBackground(field.key)?.gradient?.stops?.length || 0) <= 2
                  "
                  (click)="removeGradientStop(field.key, index)"
                >
                  -
                </button>
              </div>
              <button
                type="button"
                class="stop-btn"
                [disabled]="
                  useParentTheme ||
                  (getBackground(field.key)?.gradient?.stops?.length || 0) >= 5
                "
                (click)="addGradientStop(field.key)"
              >
                {{ 'ADD_COLOR_STOP' | translate }}
              </button>
            </div>
          </div>
        </ng-container>
      </div>
    </section>

    <section>
      <h3>{{ 'HEADER_SETTINGS' | translate }}</h3>
      <div class="field-grid">
        <div
          class="theme-field"
          *ngFor="let field of headerFields"
        >
          <label>{{ field.label | translate }}</label>
          <select class="form-select" [formControlName]="field.key">
            <option [ngValue]="true">{{ 'YES' | translate }}</option>
            <option [ngValue]="false">{{ 'NO' | translate }}</option>
          </select>
        </div>
        <div class="theme-field">
          <label for="languageSelectorPosition">
            {{ 'LANGUAGE_SELECTOR_POSITION' | translate }}
          </label>
          <select
            id="languageSelectorPosition"
            class="form-select"
            formControlName="languageSelectorPosition"
          >
            <option value="navbar">Navbar</option>
            <option value="footer">Footer</option>
          </select>
        </div>
      </div>
    </section>
  </form>
</div>
